import {toast} from "react-toastify";
import {useSelector} from "react-redux";
import React, {Fragment, useEffect, useState} from 'react';
import {Alert, Badge, Form, Spinner, Table} from "react-bootstrap";
import EventBus from "../../../../app/eventbus/EventBus";
import billingService from "../../../../services/billingService";
import {getPackageCodeLabel} from "../../../../enum/BillingPackageCode";
import {getBillingErrorMessage} from "../../../../helpers/billing-response";
import {formatBillingMoney, formatPackageDuration} from "../../../../helpers/billing-format";
import {selectActiveOrganization} from "../../../../app/store/reducers/auth/authReducer";
import {selectLang, selectSystemMoneyFormat, Translate} from "../../../../app/store/reducers/main/mainReducer";
import {
    SET_ORGANIZATION_PACKAGE_VISIBILITY_FAILED,
    SET_ORGANIZATION_PACKAGE_VISIBILITY_SUCCEED
} from "../../../../app/eventbus/billingEvents";

/**
 * Tashkilot uchun tariflarning ko'rinishi (AdminOrganizationPackageVisibility).
 *
 * Ro'yxat butun katalogni hisoblangan is_hidden bilan qaytaradi:
 *   has_override = true  — ushbu tashkilot uchun maxsus istisno saqlangan;
 *   has_override = false — paketning standart holati amal qilmoqda.
 *
 * Switch "Ko'rinadi" holatini boshqaradi, ya'ni is_hidden = !checked. Backend'da is_hidden
 * *bool bo'lgani uchun har doim aniq true/false yuboriladi. Istisnoni butunlay olib
 * tashlash (standartga qaytarish) API'da mavjud emas.
 */
const OrganizationPackageVisibility = ({organizationId}) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [savingCodes, setSavingCodes] = useState(() => new Set());

    const activeOrganization = useSelector(selectActiveOrganization);
    const systemMoneyFormat = useSelector(selectSystemMoneyFormat);
    const lang = useSelector(selectLang);
    const t = Translate;

    const loadPackages = async ({silent = false} = {}) => {
        if (!organizationId) return;

        if (!silent) setLoading(true);
        setLoadError(null);

        try {
            const loadedRows = await billingService.getOrganizationPackagesWithVisibility({organization_id: organizationId});
            setRows(loadedRows);
        } catch (error) {
            setRows([]);
            setLoadError(getBillingErrorMessage(error) || t(lang, 'common.toast.error'));
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        loadPackages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, activeOrganization]);

    const onVisibilityChange = (row, isVisible) => {
        const packageCode = row?.package?.code;

        // Ayni shu qator uchun so'rov ketayotgan bo'lsa, takroriy POST yubormaymiz.
        if (!packageCode || !organizationId || savingCodes.has(packageCode)) return;

        const payload = {
            organization_id: organizationId,
            package_code: packageCode,
            // Backend'da *bool — hech qachon undefined yubormaymiz.
            is_hidden: !isVisible,
        };

        setSavingCodes(prev => new Set(prev).add(packageCode));
        billingService.setOrganizationPackageVisibility(payload)
            .then((visibility) => {
                EventBus.dispatch(SET_ORGANIZATION_PACKAGE_VISIBILITY_SUCCEED, visibility);
                toast.success(t(lang, 'common.toast.success'));
                return loadPackages({silent: true});
            })
            .catch((error) => {
                EventBus.dispatch(SET_ORGANIZATION_PACKAGE_VISIBILITY_FAILED, error);
                toast.error(getBillingErrorMessage(error) || t(lang, 'common.toast.error'));
            })
            .finally(() => setSavingCodes(prev => {
                const next = new Set(prev);
                next.delete(packageCode);
                return next;
            }));
    };

    return (
        <Fragment>
            <div className={'card-header'}>
                <span className={'text-muted fs--1'}>
                    {t(lang, 'admin.organization.package_visibility.description')}
                </span>
            </div>

            {!!loadError && <Alert variant={'danger'} className={'mx-3'}>{loadError}</Alert>}

            <Table size={'lg'} hover={true} responsive={true}>
                <thead className={'bg-300'}>
                <tr>
                    <th>№</th>
                    <th>{t(lang, 'admin.organization.package_visibility.datatable.column.package')}</th>
                    <th className={'text-end'}>{t(lang, 'admin.organization.package_visibility.datatable.column.cost')}</th>
                    <th>{t(lang, 'admin.organization.package_visibility.datatable.column.duration')}</th>
                    <th>{t(lang, 'admin.organization.package_visibility.datatable.column.source')}</th>
                    <th className={'text-end'}>{t(lang, 'admin.organization.package_visibility.datatable.column.visible')}</th>
                </tr>
                </thead>
                <tbody>
                {loading && (
                    <tr>
                        <td colSpan={6} className={'text-center py-4'}>
                            <Spinner animation={'border'} size={'sm'} role={'status'}/>
                        </td>
                    </tr>
                )}

                {!loading && rows.length === 0 && (
                    <tr>
                        <td colSpan={6} className={'text-center text-muted py-4'}>
                            {t(lang, 'admin.organization.package_visibility.datatable.no_data')}
                        </td>
                    </tr>
                )}

                {!loading && rows.map((row, index) => {
                    const billingPackage = row?.package || {};
                    const packageCode = billingPackage.code;
                    const isVisible = !row?.is_hidden;
                    const isSaving = savingCodes.has(packageCode);

                    return (
                        <tr key={packageCode || index}>
                            <td>{index + 1}</td>
                            <td className={'fw-semi-bold text-black'}>
                                {getPackageCodeLabel(packageCode, lang, t, billingPackage.name)}
                                {!!billingPackage.is_demo && (
                                    <Badge bg={'info'} className={'ms-2'}>demo</Badge>
                                )}
                                <div className={'text-muted fs--2'}>{packageCode}</div>
                            </td>
                            <td className={'text-end'}>{formatBillingMoney(billingPackage.cost, systemMoneyFormat)}</td>
                            <td>{formatPackageDuration(billingPackage, lang, t)}</td>
                            <td>
                                {row?.has_override ? (
                                    <Badge bg={'warning'}>
                                        {t(lang, 'admin.organization.package_visibility.source.override')}
                                    </Badge>
                                ) : (
                                    <Badge bg={'secondary'}>
                                        {t(lang, 'admin.organization.package_visibility.source.default')}
                                    </Badge>
                                )}
                            </td>
                            <td className={'text-end'}>
                                <div className={'d-flex align-items-center justify-content-end gap-2'}>
                                    {isSaving && <Spinner animation={'border'} size={'sm'} role={'status'}/>}
                                    <span className={isVisible ? 'text-success fs--1' : 'text-danger fs--1'}>
                                        {isVisible
                                            ? t(lang, 'admin.organization.package_visibility.state.visible')
                                            : t(lang, 'admin.organization.package_visibility.state.hidden')}
                                    </span>
                                    <Form.Check type={'switch'}
                                                className={'mb-0'}
                                                id={`package-visibility-${packageCode || index}`}
                                                checked={isVisible}
                                                disabled={isSaving || !packageCode}
                                                onChange={(event) => onVisibilityChange(row, event.target.checked)}
                                    />
                                </div>
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </Table>
        </Fragment>
    );
};

export default OrganizationPackageVisibility;
